import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  MessagesSquare,
  Calendar,
  Users,
  Bot,
  UserCog,
  Blocks,
  Settings,
  ClipboardList,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import { RuvilyMark } from './ui';

const NAV = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/conversas', icon: MessagesSquare, label: 'Conversas', badgeKey: 'waitingCount' },
  { to: '/agenda', icon: Calendar, label: 'Agenda' },
  { to: '/clientes', icon: Users, label: 'Clientes' },
  { to: '/bots', icon: Bot, label: 'Bots' },
  { to: '/equipe', icon: UserCog, label: 'Equipe' },
  { to: '/formularios', icon: ClipboardList, label: 'Formulários' },
  { to: '/integracoes', icon: Blocks, label: 'Integrações' },
  { to: '/configuracoes', icon: Settings, label: 'Configurações' },
];

const initialsOf = (text = '') =>
  text
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || 'R';

export default function Sidebar({ onClose }) {
  const { tenant } = useAuth();
  const { waitingCount } = useNotifications();
  const badgeMap = { waitingCount };
  const company = tenant?.name || 'Ruvily';

  return (
    <aside className="flex flex-col w-[244px] h-full bg-sand-0 dark:bg-sand-900 border-r border-sand-200 dark:border-sand-800">
      <div className="h-[64px] flex items-center justify-between gap-2.5 px-5 border-b border-sand-200 dark:border-sand-800">
        <div className="flex items-center gap-2.5 min-w-0">
          <RuvilyMark size={32} />
          <span className="font-display font-bold text-[19px] tracking-tight text-sand-900 dark:text-sand-50">
            Ruvily
          </span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="lg:hidden p-1 text-sand-400 hover:text-sand-600 rounded-lg"
          >
            <X size={18} />
          </button>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map(({ to, icon: Icon, label, badgeKey }) => {
          const badgeValue = badgeKey ? badgeMap[badgeKey] : null;
          return (
            <NavLink
              key={to}
              to={to}
              data-tour={`nav-${to.slice(1)}`}
              onClick={onClose}
              className={({ isActive }) =>
                clsx(
                  'w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-[14px] font-medium transition',
                  isActive
                    ? 'bg-jade-50 dark:bg-jade-900/35 text-jade-700 dark:text-jade-300'
                    : 'text-sand-600 dark:text-sand-300 hover:bg-sand-100 dark:hover:bg-sand-800 hover:text-sand-900 dark:hover:text-sand-50',
                )
              }
            >
              <span className="flex items-center gap-3">
                <Icon size={18} />
                {label}
              </span>
              {badgeValue > 0 && (
                <span className="min-w-5 h-5 px-1.5 bg-coral-500 text-white text-[11px] font-bold rounded-full grid place-items-center">
                  {badgeValue > 99 ? '99+' : badgeValue}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3 border-t border-sand-200 dark:border-sand-800">
        <div className="flex items-center gap-3 px-2 py-2 rounded-xl">
          <span className="w-9 h-9 rounded-lg bg-jade-100 dark:bg-jade-900/50 grid place-items-center font-display font-bold text-[13px] text-jade-700 dark:text-jade-300 shrink-0">
            {initialsOf(company)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-sand-900 dark:text-sand-50 truncate">
              {company}
            </p>
            <p className="text-[11.5px] text-sand-400 truncate capitalize">
              {tenant?.plan || 'starter'}
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
}
